import { Fragment, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router";
import { z } from "zod";
import { FilledButton, OutlinedButton } from "@medoma/components/Button/Button";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { Loading } from "@medoma/components/Loading/Loading";
import { Spinner } from "@medoma/components/Spinner/Spinner";
import { formatAddress } from "@medoma/models/addresses";
import { dateInputSchema, machineFormat } from "@medoma/models/date-and-time";
import {
  getPatientFamilyName,
  getPatientGivenName,
  healthcareJourneyNumberInputSchema,
  patientNameType,
  personalIdentityNumberTypeSchema,
  type ICareEpisode,
  type IEconomicUnit,
} from "@medoma/models/patients";
import {
  localizePhoneNumber,
  standardizePhoneNumber,
} from "@medoma/models/phoneNumbers";
import { conventionalPostalCodeSchema } from "@medoma/models/postalCode";
import { siteSchema, type SiteId } from "@medoma/models/sites";
import Form from "@/components/Form/Form";
import Fieldset from "@medoma/components/Fieldset/Fieldset";
import {
  informationFieldConfigValueSchema,
  normalizePatientIdentifier,
  patientKeys,
  patientLatestCareEpisodeQueryOptions,
  registerNewCareEpisode,
  sparPersonQueryOptions,
  useEconomicUnits,
  usePatientInformationFields,
  useTeams,
} from "@/api/Patients";
import type { INewCareEpisode, ISparPerson } from "@/api/Patients";
import { knownFeatureFlagsSchema, useFeatureFlag } from "@/api/FeatureFlags";
import { useUserProfile } from "@/api/Users";
import { deducedError } from "@/Utils/ErrorUtils";
import { generateRandomUUID } from "@/Utils/UniqueId";
import AddressInput, { type AddressInputFields } from "./AddressInput";
import AreaSelect, { type AreaSelectFields } from "./AreaSelect";
import CareSummaryInput, {
  type CareSummaryInputFields,
} from "./CareSummaryInput";
import DiagnosisCodeInput, {
  type DiagnosisCodeInputFields,
} from "./DiagnosisCodeInput";
import EconomicUnitSelect, {
  type EconomicUnitSelectFields,
} from "./EconomicUnitSelect";
import ExternalCareEpisodeId, {
  type ExternalCareEpisodeIdInputFields,
} from "./ExternalCareEpisodeIdInput";
import FlagsInput, { type FlagsInputFields } from "./FlagsInput";
import GeneralInformationInput, {
  type GeneralInformationInputFields,
} from "./GeneralInformationInput";
import HomeCareCheckbox, {
  type HomeCareCheckboxFields,
} from "./HomeCareCheckbox";
import LanguageInput, { type LanguageInputFields } from "./LanguageInput";
import LifeCareCheckbox, {
  type LifeCareCheckboxFields,
} from "./LifeCareCheckbox";
import MedicalTransportNumberInput, {
  type MedicalTransportNumberInputFields,
} from "./MedicalTransportNumberInput";
import PatientIdentifierInput, {
  type PatientIdentifierInputFields,
} from "./PatientIdentifierInput";
import PatientNameInput, {
  type PatientNameInputFields,
} from "./PatientNameInput";
import PatientTypeInput, {
  type PatientTypeInputFields,
} from "./PatientTypeInput";
import PetsInput, { type PetsInputFields } from "./PetsInput";
import PhoneNumberInput, {
  type PhoneNumberInputFields,
} from "./PhoneNumberInput";
import PlaceOfAssessmentInput, {
  type PlaceOfAssessmentInputFields,
} from "./PlaceOfAssessmentInput";
import PlannedAdmissionDateTimeInput, {
  type PlannedAdmissionDateTimeInputFields,
} from "./PlannedAdmissionDateTimeInput";
import ReasonForAdmissionInput, {
  type ReasonForAdmissionInputFields,
} from "./ReasonForAdmissionInput";
import ReferringUnitInput, {
  type ReferringUnitInputFields,
} from "./ReferringUnitInput";
import RelativesInput, { type RelativesInputFields } from "./RelativesInput";
import SafetyAlarmInput, {
  type SafetyAlarmInputFields,
} from "./SafetyAlarmInput";
import TeamSelect, { type TeamSelectFields } from "./TeamSelect";
import WardInput, { type WardInputFields } from "./WardInput";
import styles from "./RegisterNewPatientForm.module.scss";

type RegisterReturningPatientFormFields = AddressInputFields &
  AreaSelectFields &
  CareSummaryInputFields &
  DiagnosisCodeInputFields &
  EconomicUnitSelectFields &
  ExternalCareEpisodeIdInputFields &
  FlagsInputFields &
  GeneralInformationInputFields &
  HomeCareCheckboxFields &
  LanguageInputFields &
  LifeCareCheckboxFields &
  MedicalTransportNumberInputFields &
  PatientIdentifierInputFields &
  PatientNameInputFields &
  PatientTypeInputFields &
  PetsInputFields &
  PhoneNumberInputFields &
  PlaceOfAssessmentInputFields &
  PlannedAdmissionDateTimeInputFields &
  ReasonForAdmissionInputFields &
  ReferringUnitInputFields &
  RelativesInputFields &
  SafetyAlarmInputFields &
  TeamSelectFields &
  WardInputFields;

const identifierFromSearchParams = (searchParams: URLSearchParams) => {
  const swedishIdentityNumber = searchParams.get("swedish-identity-number");
  const swissEMedicusNumber = searchParams.get("swiss-emedicus-number");
  const swissPatientNumber = searchParams.get("swiss-patient-number");
  const latvianPersonasKods = searchParams.get("latvian-personas-kods");
  const temporaryNumber = searchParams.get("temporary-number");

  // Identifier is required (and expected to be provided).
  // If it is not provided, there is no way forward,
  // and we want to know about this to fix the issue. I.e. crash the app.
  const identifier = z
    .string()
    .parse(
      swedishIdentityNumber ??
        swissEMedicusNumber ??
        swissPatientNumber ??
        latvianPersonasKods ??
        temporaryNumber,
    );

  const personalIdentityNumberType = (() => {
    if (temporaryNumber !== null) {
      return personalIdentityNumberTypeSchema.enum
        .SwedishTemporaryIdentityNumber;
    }
    if (swedishIdentityNumber !== null) {
      return personalIdentityNumberTypeSchema.enum
        .SwedishPersonalIdentityNumber;
    }
    if (swissEMedicusNumber !== null) {
      return personalIdentityNumberTypeSchema.enum.SwissEMedicusNumber;
    }
    if (swissPatientNumber !== null) {
      return personalIdentityNumberTypeSchema.enum.SwissPatientNumber;
    }
    if (latvianPersonasKods !== null) {
      return personalIdentityNumberTypeSchema.enum.LatvianPersonasKods;
    }
    throw new Error("No valid identifier type found");
  })();

  return { identifier, personalIdentityNumberType };
};

export const RegisterReturningPatientFormContainer = () => {
  const { t } = useLingui();
  const [searchParams] = useSearchParams();
  const { identifier, personalIdentityNumberType } =
    identifierFromSearchParams(searchParams);
  const siteId = siteSchema.shape.id.parse(searchParams.get("siteId"));

  const {
    data: careEpisode,
    isPending: isPendingCareEpisode,
    isError: isErrorCareEpisode,
    error: errorCareEpisode,
  } = useQuery(
    patientLatestCareEpisodeQueryOptions({
      identifier,
      identifierType: personalIdentityNumberType,
    }),
  );

  // SPAR (Swedish population register) is the current source of truth for name
  // and address. It only holds Swedish personal identity numbers, so skip it for
  // other identifiers. It is enrichment on top of the care episode:
  // if it fails we still let the user register with the last-known data.
  const isSwedishPersonalIdentityNumber =
    personalIdentityNumberType ===
    personalIdentityNumberTypeSchema.enum.SwedishPersonalIdentityNumber;

  const { data: isSparEnabled, isPending: isPendingSparFlag } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.UseSparForPatientRegistration,
  );

  const { data: sparPerson, isLoading: isLoadingSparPerson } = useQuery({
    ...sparPersonQueryOptions({
      siteId,
      pin: normalizePatientIdentifier({
        identifier,
        identifierType: personalIdentityNumberType,
      }),
    }),
    enabled: isSwedishPersonalIdentityNumber && isSparEnabled === true,
    // SPAR is enrichment, not required: a failure must degrade to the care
    // episode data rather than bubble to an error boundary and crash the page.
    throwOnError: false,
  });

  const {
    data: economicUnits,
    isPending: isPendingEconomicUnits,
    isError: isErrorEconomicUnits,
    error: errorEconomicUnits,
  } = useEconomicUnits(siteId, { enabled: true });

  // The SPAR person query gates on `isLoading` (not `isPending`)
  // because when it is disabled (non-Swedish identifier or flag off)
  // it stays pending forever and `isPending` would block the form.
  if (isPendingCareEpisode || isPendingSparFlag || isLoadingSparPerson) {
    return (
      <Loading message={t`Hämtar information från senaste vårdperioden`} />
    );
  }

  if (isPendingEconomicUnits) {
    return <Loading message={t`Hämtar uppgifter om enheten`} />;
  }

  if (isErrorCareEpisode) {
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta information från senaste vårdperioden.`} ${deducedError(errorCareEpisode)}`}
      />
    );
  }

  if (isErrorEconomicUnits) {
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta ekonomiska enheter.`} ${deducedError(errorEconomicUnits)}`}
      />
    );
  }

  if (economicUnits.length === 0) {
    return (
      <ErrorMessage
        message={t`Ingen ekonomisk enhet hittades för vald enhet. Kontakta support om problemet kvarstår.`}
      />
    );
  }

  return (
    <RegisterReturningPatientForm
      identifier={identifier}
      personalIdentityNumberType={personalIdentityNumberType}
      siteId={siteId}
      careEpisode={careEpisode}
      sparPerson={sparPerson}
      economicUnits={economicUnits}
    />
  );
};

const RegisterReturningPatientForm = ({
  identifier,
  personalIdentityNumberType,
  siteId,
  careEpisode,
  sparPerson,
  economicUnits,
}: {
  identifier: string;
  personalIdentityNumberType: z.infer<typeof personalIdentityNumberTypeSchema>;
  siteId: SiteId;
  careEpisode: ICareEpisode;
  sparPerson: ISparPerson | null | undefined;
  economicUnits: IEconomicUnit[];
}) => {
  const { t } = useLingui();
  const { countryCode } = useUserProfile();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    name,
    patientType,
    address,
    diagnosis,
    information: generalInformation,
    healthcareJourneyNumber: medicalTransportNumber,
    pet,
    safetyAlarmText,
    lifeCare,
    homeCare,
    relatives,
    phoneNumber,
    area,
    team,
    economicUnit,
    ward,
    externalCareEpisodeId,
    flags,
    language,
    reasonForAdmission,
    careSummary,
    referringUnit,
    placeOfAssessment,
    plannedAdmissionDateTime,
  } = careEpisode;

  const {
    addressLine1,
    postalCode,
    city,
    coordinates,
    additionalInformation: additionalAddressInformation,
    keyToPatient,
  } = address;

  const isTemporaryNumber =
    personalIdentityNumberType ===
    personalIdentityNumberTypeSchema.enum.SwedishTemporaryIdentityNumber;

  // If we receive a postal code from SPAR and it differs from the postal code in the care episode, we consider
  // the patient to have moved and we don't use the area or team from the care episode as default values
  const sparPostalCode = sparPerson?.address?.postalCode;
  const postalCodeChanged = !!sparPostalCode && postalCode !== sparPostalCode;

  // Preference order for name and address: SPAR (current registry) > care
  // episode (last-known) > empty.
  const methods = useForm<RegisterReturningPatientFormFields>({
    defaultValues: {
      additionalAddressInformation: additionalAddressInformation ?? undefined,
      keyToPatient: keyToPatient ?? undefined,
      // SPAR splits the street across Utdelningsadress1/2 (-> addressLine1/2).
      // For short addresses (the common case) the street sits in addressLine2
      // and addressLine1 is empty, so prefer addressLine2 for our single field.
      addressLine1:
        sparPerson?.address?.addressLine2 ??
        sparPerson?.address?.addressLine1 ??
        addressLine1,
      areaId: postalCodeChanged ? undefined : area?.id.toString(),
      teamId: postalCodeChanged ? undefined : team?.id.toString(),
      economicUnitId: economicUnit?.id.toString(),
      city: sparPerson?.address?.city ?? city,
      coordinates,
      diagnosisCode: diagnosis?.code,
      flags: flags ?? undefined,
      generalInformation: generalInformation ?? undefined,
      homeCare: homeCare ?? undefined,
      identifier,
      isTemporaryNumber,
      lifeCare: lifeCare ?? undefined,
      medicalTransportNumber: healthcareJourneyNumberInputSchema.encode(
        medicalTransportNumber ?? null,
      ),
      givenName: sparPerson?.givenName ?? getPatientGivenName(name),
      familyName: sparPerson?.familyName ?? getPatientFamilyName(name),
      patientType,
      pets: pet ?? undefined,
      phoneNumber: phoneNumber
        ? localizePhoneNumber({ countryCode, phoneNumber })
        : undefined,
      postalCode: sparPerson?.address?.postalCode ?? postalCode,
      relatives: relatives.map((relative) => ({
        ...relative,
        phoneNumber: relative.phoneNumber
          ? localizePhoneNumber({
              countryCode,
              phoneNumber: relative.phoneNumber,
            })
          : undefined,
      })),
      safetyAlarmText: safetyAlarmText ?? undefined,
      wardId: ward?.id.toString(),
      externalCareEpisodeId: undefined,
      language: language ?? undefined,
      reasonForAdmission: reasonForAdmission ?? undefined,
      careSummary: careSummary ?? undefined,
      referringUnit: referringUnit ?? undefined,
      placeOfAssessment: placeOfAssessment ?? undefined,
      plannedAdmissionDateTime: plannedAdmissionDateTime
        ? machineFormat(plannedAdmissionDateTime, "yyyy-MM-dd")
        : "",
    },
  });

  const { setError, reset, getValues, setValue } = methods;

  const {
    data: conditionalInformationFields,
    isPending: isPendingConditionalInformationFields,
    isError: isErrorConditionalInformationFields,
    error: errorConditionalInformationFields,
  } = usePatientInformationFields();

  const { data: areaEnabled } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.Area,
  );

  const { data: wardEnabled } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.CardiologyWorkflow,
  );

  const { data: teams } = useTeams(siteId);

  useEffect(() => {
    // Only one unit at this site — always use it, regardless of what the old care episode had.
    if (economicUnits.length === 1 && economicUnits[0]) {
      setValue("economicUnitId", economicUnits[0].id.toString());
    } else if (economicUnits.length > 1) {
      // Multiple units — keep the old value only if it's actually one of this site's units.
      // If the old care episode was from a different site its unit won't be in this list,
      // and we must clear it so the required validation forces the user to make a choice.
      const existingEconomicUnitExistsAtNewSite = economicUnits.some(
        (newSiteEconomicUnit) => newSiteEconomicUnit.id === economicUnit?.id,
      );
      if (!existingEconomicUnitExistsAtNewSite) {
        setValue("economicUnitId", "");
      }
    }
  }, [economicUnits, economicUnit, setValue]);

  const { mutate, isPending: isPendingRegisterNewCareEpisode } = useMutation({
    mutationFn: (newCareEpisode: INewCareEpisode) =>
      registerNewCareEpisode(newCareEpisode),
    onSuccess: async (_, newCareEpisode) => {
      await queryClient.invalidateQueries({ queryKey: patientKeys.lists() });
      void navigate(`/patients/${newCareEpisode.careEpisodeId}`, {
        replace: true,
      });
    },
    onError: (error) => {
      setError("root.server", {
        message: deducedError(error),
      });
      // Reset `isDirty` to support only showing server error when the form is not changed.
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
  });

  if (isPendingConditionalInformationFields) {
    return <Loading message={t`Hämtar information om valbara fält`} />;
  }

  if (isErrorConditionalInformationFields) {
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta information om valbara fält.`} ${deducedError(errorConditionalInformationFields)}`}
      />
    );
  }

  const otherInformationFields = [
    {
      condition:
        conditionalInformationFields.ReferringUnit !==
        informationFieldConfigValueSchema.enum.hidden,
      component: (
        <ReferringUnitInput
          referringUnitFieldConfig={conditionalInformationFields.ReferringUnit}
        />
      ),
    },
    {
      condition:
        conditionalInformationFields.PlaceOfAssessment !==
        informationFieldConfigValueSchema.enum.hidden,
      component: (
        <PlaceOfAssessmentInput
          placeOfAssessmentFieldConfig={
            conditionalInformationFields.PlaceOfAssessment
          }
        />
      ),
    },
    {
      condition:
        conditionalInformationFields.DiagnosisCode !==
          informationFieldConfigValueSchema.enum.hidden && diagnosis,
      component: (
        <DiagnosisCodeInput
          diagnosisCodeFieldConfig={conditionalInformationFields.DiagnosisCode}
        />
      ),
    },
    {
      condition:
        conditionalInformationFields.Flags !==
        informationFieldConfigValueSchema.enum.hidden,
      component: (
        <FlagsInput flagsFieldConfig={conditionalInformationFields.Flags} />
      ),
    },
    {
      condition:
        conditionalInformationFields.GeneralInformation !==
          informationFieldConfigValueSchema.enum.hidden && generalInformation,
      component: (
        <GeneralInformationInput
          generalInformationFieldConfig={
            conditionalInformationFields.GeneralInformation
          }
        />
      ),
    },
    {
      condition:
        conditionalInformationFields.MedicalTransportNumber !==
          informationFieldConfigValueSchema.enum.hidden &&
        medicalTransportNumber,
      component: (
        <MedicalTransportNumberInput
          medicalTransportNumberFieldConfig={
            conditionalInformationFields.MedicalTransportNumber
          }
        />
      ),
    },
    {
      condition:
        conditionalInformationFields.Pet !==
          informationFieldConfigValueSchema.enum.hidden && pet,
      component: (
        <PetsInput petsFieldConfig={conditionalInformationFields.Pet} />
      ),
    },
    {
      condition:
        conditionalInformationFields.PersWristband !==
          informationFieldConfigValueSchema.enum.hidden && safetyAlarmText,
      component: (
        <SafetyAlarmInput
          safetyAlarmFieldConfig={conditionalInformationFields.PersWristband}
        />
      ),
    },
    {
      condition:
        conditionalInformationFields.LifeCare !==
          informationFieldConfigValueSchema.enum.hidden &&
        lifeCare !== undefined,
      component: <LifeCareCheckbox />,
    },
    {
      condition:
        conditionalInformationFields.HomeCare !==
          informationFieldConfigValueSchema.enum.hidden &&
        homeCare !== undefined,
      component: <HomeCareCheckbox />,
    },
    {
      condition:
        conditionalInformationFields.Language !==
          informationFieldConfigValueSchema.enum.hidden && language,
      component: (
        <LanguageInput
          languageFieldConfig={conditionalInformationFields.Language}
        />
      ),
    },
    {
      condition:
        conditionalInformationFields.PlannedAdmissionDateTime !==
        informationFieldConfigValueSchema.enum.hidden,
      component: (
        <PlannedAdmissionDateTimeInput
          plannedAdmissionDateTimeFieldConfig={
            conditionalInformationFields.PlannedAdmissionDateTime
          }
        />
      ),
    },
    {
      condition:
        conditionalInformationFields.ReasonForAdmission !==
        informationFieldConfigValueSchema.enum.hidden,
      component: (
        <ReasonForAdmissionInput
          reasonForAdmissionFieldConfig={
            conditionalInformationFields.ReasonForAdmission
          }
        />
      ),
    },
    {
      condition:
        conditionalInformationFields.CareSummary !==
        informationFieldConfigValueSchema.enum.hidden,
      component: (
        <CareSummaryInput
          careSummaryFieldConfig={conditionalInformationFields.CareSummary}
        />
      ),
    },
  ];

  const hasAnyOtherInformation = otherInformationFields.some(
    (field) => field.condition,
  );

  const validateAndSubmit = methods.handleSubmit((data) => {
    const relativesWithStandardizedPhoneNumbers = data.relatives.map(
      (relative) => ({
        ...relative,
        phoneNumber: relative.phoneNumber
          ? standardizePhoneNumber({
              countryCode,
              phoneNumber: relative.phoneNumber,
            })
          : null,
      }),
    );
    const newCareEpisode: INewCareEpisode = {
      careEpisodeId: generateRandomUUID(),
      address: formatAddress({
        addressLine1: data.addressLine1,
        postalCode: conventionalPostalCodeSchema.shape[countryCode].parse(
          data.postalCode,
        ),
        city: data.city,
        coordinates: data.coordinates,
        // The address input calls the field `additionalAddressInformation`; the address
        // itself calls it `additionalInformation`.
        additionalInformation: data.additionalAddressInformation,
        keyToPatient: data.keyToPatient,
        countryCode,
      }),
      siteId,
      area: data.areaId ? Number(data.areaId) : null,
      team: data.teamId ? Number(data.teamId) : null,
      economicUnit: Number(data.economicUnitId),
      diagnosisCode: data.diagnosisCode,
      flags: data.flags,
      healthcareJourneyNumber: healthcareJourneyNumberInputSchema.decode(
        data.medicalTransportNumber,
      ),
      homeCare: data.homeCare,
      information: data.generalInformation,
      lifeCare: data.lifeCare,
      name: {
        type: patientNameType.enum.GivenAndFamily,
        givenName: data.givenName,
        familyName: data.familyName,
      },
      patientType: data.patientType,
      personalIdentityNumber: normalizePatientIdentifier({
        identifier: data.identifier,
        identifierType: personalIdentityNumberType,
      }),
      personalIdentityNumberType,
      pet: data.pets,
      // Optional at registration, so an untouched field must travel as "not given" rather
      // than as an empty phone number.
      phoneNumber: data.phoneNumber
        ? standardizePhoneNumber({ countryCode, phoneNumber: data.phoneNumber })
        : null,
      relatives: relativesWithStandardizedPhoneNumbers,
      safetyAlarmText: data.safetyAlarmText,
      ward: data.wardId ? Number(data.wardId) : null,
      externalCareEpisodeId: data.externalCareEpisodeId,
      language: data.language,
      plannedAdmissionDateTime: dateInputSchema.decode(
        data.plannedAdmissionDateTime,
      ),
      reasonForAdmission: data.reasonForAdmission,
      careSummary: data.careSummary,
      referringUnit: data.referringUnit,
      placeOfAssessment: data.placeOfAssessment,
    };

    mutate(newCareEpisode);
  });

  return (
    <FormProvider {...methods}>
      <Form onSubmit={validateAndSubmit}>
        {/* REQUIRED FIELDS FOR REGISTERING A PROSPECT PATIENT */}
        <PatientIdentifierInput readonly />
        {/* ExternalCareEpisodeId is optional, but should be placed after the identifier input */}
        {conditionalInformationFields.ExternalCareEpisodeId !==
          informationFieldConfigValueSchema.enum.hidden &&
        externalCareEpisodeId ? (
          <ExternalCareEpisodeId
            externalCareEpisodeIdFieldConfig={
              conditionalInformationFields.ExternalCareEpisodeId
            }
          />
        ) : null}
        <PatientNameInput />
        {wardEnabled ? <WardInput siteId={siteId} /> : null}
        <PatientTypeInput />
        <PhoneNumberInput isRequired={false} />
        <AddressInput
          siteId={siteId}
          additionalAddressInformationFieldConfig={
            conditionalInformationFields.AdditionalAdressInformation
          }
          keyToPatientFieldConfig={conditionalInformationFields.KeyToPatient}
        />
        {areaEnabled ? <AreaSelect siteId={siteId} /> : null}
        {teams && teams.length > 0 ? <TeamSelect siteId={siteId} /> : null}
        {economicUnits.length > 1 ? (
          <EconomicUnitSelect siteId={siteId} />
        ) : null}
        {/* OTHER FIELDS, DISPLAYED IF THEY ARE ACTIVATED ON THE ORGANIZATION AND HAVE DATA FROM THE CARE EPISODE */}
        {hasAnyOtherInformation ? (
          <Fieldset legend={t`Övrig information`} visualStyle="framed">
            <Form.Section>
              {otherInformationFields
                .filter((field) => field.condition)
                .map((field, index) => (
                  <Fragment key={index}>{field.component}</Fragment>
                ))}
            </Form.Section>
          </Fieldset>
        ) : null}
        {conditionalInformationFields.CloseContact !==
          informationFieldConfigValueSchema.enum.hidden &&
        relatives &&
        relatives.length > 0 ? (
          <RelativesInput
            relativesFieldConfig={conditionalInformationFields.CloseContact}
          />
        ) : null}

        {methods.formState.errors.root?.server?.message ? (
          <ErrorMessage
            message={methods.formState.errors.root.server.message}
          />
        ) : null}

        <div className={styles.buttonContainer}>
          <FilledButton
            type="submit"
            width="fill-container"
            disabled={isPendingRegisterNewCareEpisode}
          >
            {isPendingRegisterNewCareEpisode ? <Spinner /> : null}
            <Trans>Registrera patient</Trans>
          </FilledButton>
          <OutlinedButton
            type="button"
            width="fill-container"
            onClick={() => navigate("/patients")}
          >
            <Trans>Avbryt</Trans>
          </OutlinedButton>
        </div>
      </Form>
    </FormProvider>
  );
};
